/* eslint-disable complexity */
import { useCallback } from 'react';

import { classifyCombinablePromotions } from '@/components/blocks/FreeGiftV2/utils';
import { useCartSWRKey } from '@/data/Content/Cart';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useSettings } from '@/data/Settings';
import { Order } from '@/data/types/Order';
import { CombinablePromotion } from '@/data/types/Promotions';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';

import { useGetCategories } from 'shared/custom/hooks/Categories';
import { findParentCategories } from 'shared/custom/utils/getCategoryByIdForGA';

import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import {
	PromotionDetails,
	useGetPromotionDetails,
} from 'presentation/events/hooks/useGetPromotionDetails';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { buildChatbotEventParams } from 'presentation/events/hooks/useTrackDibot';
import { saveAddToCartPromotionDetailsToSessionStorage } from 'presentation/events/services/promotionsService';
import { setCookieCAPI } from 'presentation/events/utils/eventCookieUtils';
import { getItemPartNumber } from 'presentation/events/utils/productUtils';
import { unstable_serialize as serialize, useSWRConfig } from 'swr';

import { SendAddToCartEventProps, TrackAddToCartProps } from './types';
import { useTrackAddToCartFacebook } from './useTrackAddToCartFacebook';
import { useTrackAddToCartGA4 } from './useTrackAddToCartGA4';
import { useTrackAddToCartPinterest } from './useTrackAddToCartPinterest';
import { useTrackAddToCartTikTok } from './useTrackAddToCartTikTok';
import { enrichProductWithFreeGift } from './utils';
import { buildDeliveryDaysEventParams } from 'presentation/events/utils/ga4Utils';

export const useTrackAddToCart = () => {
	const trackException = useTrackException();
	const getPromotionDetails = useGetPromotionDetails();
	const { navigation } = useGetCategories();
	const router = useNextRouter();
	const { settings } = useSettings();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const { storeId } = getClientSideCommon(settings, router);
	const cartSWRKey = useCartSWRKey();
	const { cache } = useSWRConfig();

	const sendGA4Event = useTrackAddToCartGA4();
	const sendFacebookEvent = useTrackAddToCartFacebook();
	const sendPinterestEvent = useTrackAddToCartPinterest();
	const sendTikTokEvent = useTrackAddToCartTikTok();

	return useCallback(
		async (params: TrackAddToCartProps) => {
			try {
				const eventId = setCookieCAPI('mfbt');

				const chatbotEventParams = buildChatbotEventParams({
					isChatbotEvent: params.isChatbotEvent,
				});
				const reportsDeliveryDays =
					params.isEstimatedDeliveryDateDisplayed === true ||
					!!params.bundleId ||
					params.homeDeliveryDate !== undefined ||
					params.pickupDate !== undefined;

				const deliveryDateEventParams = reportsDeliveryDays
					? buildDeliveryDaysEventParams({
							homeDeliveryDate: params.homeDeliveryDate,
							pickupDate: params.pickupDate,
					  })
					: undefined;
				// Get categories for product or items

				let sendAddToCartProps: SendAddToCartEventProps | undefined;
				const cartData = cache.get(serialize(cartSWRKey))?.data as Order;
				const cartItems = params.cachedCartItems ?? cartData?.orderItem ?? [];

				const cartItemsPromotionDetails = new Map<string, PromotionDetails>();
				if (cartItems.length > 0) {
					const results = await Promise.all(
						cartItems.map(async (item) => {
							const details = await getPromotionDetails({ catentryId: item.productId });
							return { productId: item.productId, details };
						})
					);
					results.forEach(({ productId, details }) => {
						if (details) {
							cartItemsPromotionDetails.set(productId, details);
						}
					});
				}

				const normalizedCartItems = cartItems.map((item) => ({
					partNumber: item.partNumber,
					productId: item.productId,
					quantity: item.quantity,
				}));

				if (params.product) {
					// Single product

					const promotionDetails = await getPromotionDetails({
						catentryId: params.product.id,
					});

					if (promotionDetails) {
						cartItemsPromotionDetails.set(params.product.id, promotionDetails);
					}

					const categories =
						params.categories ??
						findParentCategories(navigation, params.product.parentCatalogGroupID);

					const enrichedProduct = enrichProductWithFreeGift({
						product: params.product,
						quantity: params.quantity,
						physicalStoreId: physicalStoreId ?? '',
						cartItems: normalizedCartItems,
						cartItemsPromotionDetails,
					});

					const product = {
						...enrichedProduct,
						promoThdCode:
							enrichedProduct.promoThdCode ??
							classifyCombinablePromotions(
								promotionDetails?.combinablePromotions as CombinablePromotion[] | undefined
							),
					};

					sendAddToCartProps = {
						...params,
						product,
						categories,
						eventId,
						storeId,
						chatbotEventParams,
						promotionDetails: promotionDetails ?? undefined,
						deliveryDateEventParams: deliveryDateEventParams ?? undefined,
					};

					saveAddToCartPromotionDetailsToSessionStorage(getItemPartNumber(params.product));
				}

				if (params.items) {
					// Multiple products

					const ps = params.items.map(async (item) => {
						const promotionDetails = await getPromotionDetails({
							catentryId: item.product.id,
						});

						if (promotionDetails) {
							cartItemsPromotionDetails.set(item.product.id, promotionDetails);
						}

						const categories =
							item.categories ??
							findParentCategories(navigation, item.product.parentCatalogGroupID);

						saveAddToCartPromotionDetailsToSessionStorage(getItemPartNumber(item.product));

						const enrichedProduct = enrichProductWithFreeGift({
							product: item.product,
							quantity: item.quantity,
							physicalStoreId: physicalStoreId ?? '',
							cartItems: normalizedCartItems,
							cartItemsPromotionDetails,
						});

						const product = {
							...enrichedProduct,
							promoThdCode:
								enrichedProduct.promoThdCode ??
								classifyCombinablePromotions(
									promotionDetails?.combinablePromotions as CombinablePromotion[] | undefined
								),
						};

						return {
							...item,
							product,
							categories,
							promotionDetails: promotionDetails ?? undefined,
						};
					});

					const items = await Promise.all(ps);

					sendAddToCartProps = {
						...params,
						items,
						eventId,
						storeId,
						chatbotEventParams,
						deliveryDateEventParams: deliveryDateEventParams ?? undefined,
					};
				}

				if (!sendAddToCartProps) {
					throw new Error('Invalid trackAddToCartProps');
				}

				// Send events
				sendGA4Event(sendAddToCartProps);

				sendFacebookEvent(sendAddToCartProps);

				sendPinterestEvent(sendAddToCartProps);

				sendTikTokEvent(sendAddToCartProps);
			} catch (e) {
				console.error('Error sending add to cart event', e);
				trackException(e);
			}
		},
		[
			cache,
			cartSWRKey,
			storeId,
			physicalStoreId,
			sendGA4Event,
			sendFacebookEvent,
			sendPinterestEvent,
			sendTikTokEvent,
			getPromotionDetails,
			navigation,
			trackException,
		]
	);
};
